import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BedDouble,
  Briefcase,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Mail,
  MapPin,
  Phone,
  Plane,
  UserRound,
  Users,
} from "lucide-react";
import CampaignPassengerPricingSummary from "./CampaignPassengerPricingSummary";
import HeroGalleryLightbox from "../HeroGalleryLightbox/HeroGalleryLightbox";
import { formatBelvedereCampaignAirportLabel } from "../../services/belvedereFlightSearch";
import { formatIls } from "../../services/belvederePricing";
import { stripCampaignBoardFromRoomName } from "../../utils/campaignHotelProfileRooms";
import "../../Pages/campaigns/BelvedereCampaignOfferPage.scss";

function OfferFlightLegCard({ leg, index }) {
  const label = leg.labelHe || (index === 0 ? "הלוך" : "חזור");
  const from =
    String(leg.originLabel || "").trim() ||
    formatBelvedereCampaignAirportLabel(leg.originCode);
  const to =
    String(leg.destinationLabel || "").trim() ||
    formatBelvedereCampaignAirportLabel(leg.destinationCode);
  const airlineName =
    leg.airlineName && leg.airlineName !== "—" ? leg.airlineName : null;
  const flightNumber = String(leg.flightNumber || "").trim();

  return (
    <article className="belvedere-offer__leg-card" aria-label={label}>
      <div className="belvedere-offer__leg-card-top">
        <span className="belvedere-offer__leg-dir">{label}</span>
        {leg.depDateDisplay ? (
          <span className="belvedere-offer__leg-date">{leg.depDateDisplay}</span>
        ) : null}
      </div>
      <div className="belvedere-offer__leg-times">
        <div>
          <strong>{leg.departureTime || "—"}</strong>
          <span>{from}</span>
        </div>
        <span className="belvedere-offer__leg-arrow" aria-hidden>
          →
        </span>
        <div>
          <strong>{leg.arrivalTime || "—"}</strong>
          <span>{to}</span>
        </div>
      </div>
      <div className="belvedere-offer__leg-meta">
        {airlineName ? (
          <span className="belvedere-offer__leg-airline">{airlineName}</span>
        ) : null}
        {flightNumber ? (
          <span className="belvedere-offer__leg-flight-no">טיסה {flightNumber}</span>
        ) : null}
      </div>
    </article>
  );
}

function resolveOfferRoomDisplayName(room) {
  const value = String(room?.name || "").trim();
  if (!value) return null;
  if (/^חדר\s+\d+$/i.test(value)) return null;
  if (/^חבילת\s+\d+\s+חדרים$/i.test(value)) return null;
  return value;
}

function formatOfferRoomBreakdownLabel(room) {
  const roomName = resolveOfferRoomDisplayName(room);
  const occupancy = String(room?.occupancyLabel || "").trim() || null;
  const label = [roomName, occupancy].filter(Boolean).join(" · ");
  if (label) return label;
  const index = Number(room?.roomIndex);
  return Number.isFinite(index) && index > 0 ? `חדר ${index}` : "חדר";
}

function OfferStayRooms({ offer, nightsLabel }) {
  const roomDetails = Array.isArray(offer.roomDetails) ? offer.roomDetails : [];
  const isMultiRoom = roomDetails.length > 1;

  if (isMultiRoom) {
    return (
      <div className="belvedere-offer__stay-multi">
        <ul className="belvedere-offer__room-list" aria-label="פירוט חדרים">
          {roomDetails.map((room) => {
            const roomName = resolveOfferRoomDisplayName(room);
            const shareIls = Number(room.packageWeightedIls);
            const hasShare = Number.isFinite(shareIls) && shareIls > 0;
            return (
              <li key={room.roomIndex} className="belvedere-offer__room-item">
                {room.imageUrl ? (
                  <img
                    className="belvedere-offer__room-item-img"
                    src={room.imageUrl}
                    alt={roomName || `חדר ${room.roomIndex}`}
                    loading="lazy"
                  />
                ) : null}
                <div className="belvedere-offer__room-item-body">
                  <span className="belvedere-offer__room-item-index">
                    חדר {room.roomIndex}
                  </span>
                  {roomName ? <strong>{roomName}</strong> : null}
                  {room.occupancyLabel ? (
                    <span className="belvedere-offer__room-item-occ">
                      {room.occupancyLabel}
                    </span>
                  ) : null}
                  {hasShare ? (
                    <span className="belvedere-offer__room-item-share">
                      {formatIls(shareIls)}
                    </span>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
        <div className="belvedere-offer__stay-facts">
          <div className="belvedere-offer__fact">
            <span>תאריכי שהייה</span>
            <strong>
              {offer.checkInLabel} – {offer.checkOutLabel}
              {nightsLabel ? ` (${nightsLabel})` : ""}
            </strong>
          </div>
          <div className="belvedere-offer__fact">
            <span>מיקום</span>
            <strong>{offer.hotelLocation}</strong>
          </div>
          <p className="belvedere-offer__board">{offer.boardLabel}</p>
        </div>
      </div>
    );
  }

  const single = roomDetails[0];
  const roomImage = single?.imageUrl || offer.roomImage;
  const roomName = stripCampaignBoardFromRoomName(single?.name || offer.roomName, "");
  const occupancyLabel = single?.occupancyLabel || offer.occupancyLabel;

  return (
    <div className="belvedere-offer__stay-grid">
      {roomImage ? (
        <img
          className="belvedere-offer__room-img"
          src={roomImage}
          alt={roomName || "חדר"}
          loading="lazy"
        />
      ) : null}
      <div className="belvedere-offer__stay-facts">
        {roomName &&
        !/^חדר\s+\d+$/i.test(String(roomName)) &&
        !/^חבילת\s+\d+\s+חדרים$/i.test(String(roomName)) ? (
          <div className="belvedere-offer__fact">
            <span>סוג חדר</span>
            <strong>{roomName}</strong>
          </div>
        ) : null}
        <div className="belvedere-offer__fact">
          <span>תאריכי שהייה</span>
          <strong>
            {offer.checkInLabel} – {offer.checkOutLabel}
            {nightsLabel ? ` (${nightsLabel})` : ""}
          </strong>
        </div>
        {occupancyLabel ? (
          <div className="belvedere-offer__fact">
            <span>הרכב נוסעים</span>
            <strong>{occupancyLabel}</strong>
          </div>
        ) : null}
        <div className="belvedere-offer__fact">
          <span>מיקום</span>
          <strong>{offer.hotelLocation}</strong>
        </div>
        <p className="belvedere-offer__board">{offer.boardLabel}</p>
      </div>
    </div>
  );
}

function OfferAgentCard({ agentBranding }) {
  if (!agentBranding) return null;
  const { logoUrl, agencyName, agentName, phone, email } = agentBranding;
  if (!logoUrl && !agencyName && !agentName && !phone && !email) return null;

  return (
    <section className="belvedere-offer__agent" aria-label="פרטי הסוכן">
      <div className="belvedere-offer__section-head">
        <UserRound size={18} aria-hidden />
        <h2>פרטי הסוכן</h2>
      </div>
      <div className="belvedere-offer__agent-body">
        {logoUrl ? (
          <img
            className="belvedere-offer__agent-logo"
            src={logoUrl}
            alt={agencyName || "לוגו סוכנות"}
            loading="lazy"
          />
        ) : null}
        <div className="belvedere-offer__agent-grid">
          {agentName ? (
            <div className="belvedere-offer__agent-item">
              <span>שם הסוכן</span>
              <strong>{agentName}</strong>
            </div>
          ) : null}
          {agencyName ? (
            <div className="belvedere-offer__agent-item">
              <span>סוכנות</span>
              <strong>{agencyName}</strong>
            </div>
          ) : null}
          {phone ? (
            <div className="belvedere-offer__agent-item">
              <span>טלפון</span>
              <a href={`tel:${phone}`}>
                <Phone size={14} aria-hidden />
                {phone}
              </a>
            </div>
          ) : null}
          {email ? (
            <div className="belvedere-offer__agent-item">
              <span>אימייל</span>
              <a href={`mailto:${email}`}>
                <Mail size={14} aria-hidden />
                {email}
              </a>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export function OfferSimpleGallery({ images, hotelName, title = "גלריית המלון" }) {
  const list = useMemo(
    () => (Array.isArray(images) ? images.filter((img) => img?.src) : []),
    [images],
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    setActiveIndex(0);
  }, [list.length, list[0]?.src]);

  const goToImage = useCallback(
    (delta) => {
      if (list.length <= 1) return;
      setActiveIndex((idx) => (idx + delta + list.length) % list.length);
    },
    [list.length],
  );

  if (!list.length) return null;

  const active = list[Math.min(activeIndex, list.length - 1)];
  const galleryUrls = list.map((img) => img.src);

  return (
    <div className="belvedere-offer__gallery belvedere-offer__gallery--simple">
      <div className="belvedere-offer__section-head">
        <h2>{title}</h2>
        <span className="belvedere-offer__section-tag">
          {list.length} תמונות
        </span>
      </div>

      <div className="belvedere-offer__gallery-slider">
        <button
          type="button"
          className="belvedere-offer__gallery-main-btn"
          onClick={() => setLightboxOpen(true)}
          aria-label="פתיחת צפייה מלאה בגלריה"
        >
          <img
            src={active.src}
            alt={active.altHe || hotelName || "תמונת מלון"}
            loading="eager"
          />
        </button>

        {list.length > 1 ? (
          <>
            <button
              type="button"
              className="belvedere-offer__gallery-nav belvedere-offer__gallery-nav--prev"
              onClick={() => goToImage(-1)}
              aria-label="תמונה קודמת"
            >
              <ChevronRight size={20} aria-hidden />
            </button>
            <button
              type="button"
              className="belvedere-offer__gallery-nav belvedere-offer__gallery-nav--next"
              onClick={() => goToImage(1)}
              aria-label="תמונה הבאה"
            >
              <ChevronLeft size={20} aria-hidden />
            </button>
            <span className="belvedere-offer__gallery-counter" aria-live="polite">
              {activeIndex + 1} / {list.length}
            </span>
          </>
        ) : null}
      </div>

      {list.length > 1 ? (
        <div
          className="belvedere-offer__gallery-thumbs"
          role="listbox"
          aria-label="בחירת תמונה"
        >
          {list.map((image, index) => (
            <button
              key={image.id || `${image.src}-${index}`}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              className={
                "belvedere-offer__gallery-thumb" +
                (index === activeIndex ? " is-active" : "")
              }
              onClick={() => setActiveIndex(index)}
              aria-label={`תמונה ${index + 1}`}
            >
              <img
                src={image.src}
                alt=""
                loading={index === 0 ? "eager" : "lazy"}
              />
            </button>
          ))}
        </div>
      ) : null}

      <HeroGalleryLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={galleryUrls}
        startIndex={activeIndex}
        onExit={(idx) => setActiveIndex(idx)}
        hotelName={hotelName || ""}
      />
    </div>
  );
}

function OfferLeadInterestForm({ primaryCta }) {
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [localError, setLocalError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  if (primaryCta?.done) {
    return (
      <div className="belvedere-offer__lead-form belvedere-offer__lead-form--success" role="status">
        <p>{primaryCta.doneLabel || "תודה, פנייתך נרשמה"}</p>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = String(fullName || "").trim();
    const phoneDigits = String(phone || "").replace(/[^\d+]/g, "");
    if (name.length < 2) {
      setLocalError("נא למלא שם מלא");
      return;
    }
    if (phoneDigits.replace(/\D/g, "").length < 8) {
      setLocalError("נא למלא מספר טלפון תקין");
      return;
    }
    if (typeof primaryCta?.onSubmit !== "function") return;
    setLocalError(null);
    setSubmitting(true);
    try {
      await primaryCta.onSubmit({
        fullName: name,
        phone: phoneDigits,
      });
    } catch (err) {
      setLocalError(
        err?.response?.data?.error?.message ||
          err?.message ||
          "לא הצלחנו לשלוח את הפרטים. נסו שוב.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const busy = Boolean(primaryCta?.loading || submitting);
  const errorText = localError || primaryCta?.error || null;

  return (
    <form
      className="belvedere-offer__lead-form"
      onSubmit={handleSubmit}
      noValidate
      dir="rtl"
      aria-label="השארת פרטים"
    >
      <h3 className="belvedere-offer__lead-form-title">
        {primaryCta?.title || "מעוניינים בהצעה?"}
      </h3>
      <p className="belvedere-offer__lead-form-hint">
        {primaryCta?.hint || "השאירו שם וטלפון — הסוכן יחזור אליכם"}
      </p>
      <label className="belvedere-offer__lead-field">
        <span>שם מלא *</span>
        <input
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
          maxLength={120}
          autoComplete="name"
          disabled={busy}
        />
      </label>
      <label className="belvedere-offer__lead-field">
        <span>טלפון *</span>
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          required
          type="tel"
          maxLength={32}
          autoComplete="tel"
          inputMode="tel"
          disabled={busy}
        />
      </label>
      {errorText ? (
        <p className="belvedere-offer__lead-error" role="alert">
          {errorText}
        </p>
      ) : null}
      <button
        type="submit"
        className="belvedere-btn belvedere-btn--primary belvedere-offer__cta"
        disabled={busy || Boolean(primaryCta?.disabled)}
      >
        {busy
          ? primaryCta?.loadingLabel || "שולחים…"
          : primaryCta?.label || "שלחו פנייה לסוכן"}
      </button>
    </form>
  );
}

/**
 * Shared customer-facing offer presentation shell (Belvedere campaign visual system).
 * Used by Royal Belvedere campaign offers and Dynamic Package public offer links.
 */
export default function CampaignCustomerOfferView({
  offer,
  kicker = null,
  brandTitle = null,
  gallery = undefined,
  primaryCta = null,
  secondaryLink = null,
  legalNote = null,
  totalHint = null,
}) {
  if (!offer) return null;

  const greeting =
    offer.customerName && offer.customerName !== "לקוח"
      ? `הצעה עבור ${offer.customerName}`
      : "הצעת מחיר אישית";
  const legs = Array.isArray(offer.flight?.legRows) ? offer.flight.legRows : [];
  const nightsLabel =
    offer.nights > 0
      ? `${offer.nights} ${offer.nights === 1 ? "לילה" : "לילות"}`
      : null;
  const resolvedKicker =
    kicker != null ? kicker : offer.kicker || "הצעת חופשה";
  const resolvedBrand =
    brandTitle != null ? brandTitle : offer.hotelName || "הצעת מחיר";
  const routeTag =
    offer.flight?.routeLabelHe ||
    (offer.flightOrigin
      ? `${offer.flightOrigin} → ${offer.flight?.destination || ""}`.trim()
      : null);
  const resolvedTotalHint =
    totalHint != null
      ? totalHint
      : `מחיר סופי לחבילה${
          offer.hasFlights ? " (מלון + טיסות" : " (מלון"
        }${offer.baggageLines?.length ? " + כבודה" : ""})`;

  const galleryNode =
    gallery === undefined ? (
      offer.galleryImages?.length ? (
        <OfferSimpleGallery
          images={offer.galleryImages}
          hotelName={offer.hotelName}
        />
      ) : null
    ) : (
      gallery
    );

  return (
    <div className="belvedere-offer" dir="rtl">
      <div className="belvedere-offer__shell">
        <header className="belvedere-offer__hero">
          {offer.heroImage ? (
            <img
              className="belvedere-offer__hero-img"
              src={offer.heroImage}
              alt={offer.hotelName || resolvedBrand}
              loading="eager"
              decoding="async"
            />
          ) : (
            <div className="belvedere-offer__hero-img belvedere-offer__hero-img--empty" />
          )}
          <div className="belvedere-offer__hero-overlay" aria-hidden />
          <div className="belvedere-offer__hero-content">
            <p className="belvedere-offer__kicker">{resolvedKicker}</p>
            <h1 className="belvedere-offer__brand">{resolvedBrand}</h1>
            <p className="belvedere-offer__greeting">{greeting}</p>
          </div>
        </header>

        <div className="belvedere-offer__glance" aria-label="סיכום מהיר">
          <div className="belvedere-offer__glance-item belvedere-offer__glance-item--dates">
            <CalendarDays size={18} aria-hidden />
            <div>
              <span>תאריכים</span>
              <strong className="belvedere-offer__glance-dates">
                <span className="belvedere-offer__glance-dates-range">
                  {offer.checkInLabel} – {offer.checkOutLabel}
                </span>
                {nightsLabel ? (
                  <span className="belvedere-offer__glance-dates-nights">
                    {nightsLabel}
                  </span>
                ) : null}
              </strong>
            </div>
          </div>
          {offer.occupancyLabel ? (
            <div className="belvedere-offer__glance-item">
              <Users size={18} aria-hidden />
              <div>
                <span>הרכב</span>
                <strong>{offer.occupancyLabel}</strong>
              </div>
            </div>
          ) : null}
          <div className="belvedere-offer__glance-item">
            <BedDouble size={18} aria-hidden />
            <div>
              <span>חדר</span>
              <strong>{stripCampaignBoardFromRoomName(offer.roomName, "—")}</strong>
            </div>
          </div>
          <div className="belvedere-offer__glance-item">
            <MapPin size={18} aria-hidden />
            <div>
              <span>בסיס אירוח</span>
              <strong>
                {offer.boardLabel}
                {offer.hotelLocation ? ` · ${offer.hotelLocation}` : ""}
              </strong>
            </div>
          </div>
        </div>

        <main className="belvedere-offer__main">
          <div className="belvedere-offer__content">
            {offer.hasFlights && legs.length > 0 ? (
              <section
                className="belvedere-offer__block belvedere-offer__flights"
                aria-label="פרטי טיסות"
              >
                <div className="belvedere-offer__section-head">
                  <Plane size={18} aria-hidden />
                  <h2>פרטי טיסות</h2>
                  {routeTag ? (
                    <span className="belvedere-offer__section-tag">{routeTag}</span>
                  ) : null}
                </div>
                <div className="belvedere-offer__legs">
                  {legs.map((leg, index) => (
                    <OfferFlightLegCard
                      key={leg.id || `${leg.labelHe || "leg"}-${index}`}
                      leg={leg}
                      index={index}
                    />
                  ))}
                </div>
                {offer.flight?.includedBaggageLines?.length > 0 ? (
                  <div
                    className="belvedere-offer__included-baggage"
                    aria-label="כבודה כלולה במחיר"
                  >
                    <div className="belvedere-offer__included-baggage-head">
                      <Briefcase size={16} aria-hidden />
                      <strong>כבודה כלולה במחיר</strong>
                    </div>
                    <ul className="belvedere-offer__included-baggage-list">
                      {offer.flight.includedBaggageLines.map((line, idx) => (
                        <li key={`${line}-${idx}`}>{line}</li>
                      ))}
                    </ul>
                  </div>
                ) : offer.flight?.includedBaggageState === "paid_upgrade" ? (
                  <p className="belvedere-offer__included-baggage-note">
                    אין כבודה כלולה במחיר הבסיס — ניתן להוסיף כבודה בתשלום נוסף
                    בקופה.
                  </p>
                ) : null}
                {offer.flight?.airlineSummary ? (
                  <p className="belvedere-offer__airline-note">
                    חברת תעופה: {offer.flight.airlineSummary}
                  </p>
                ) : null}
              </section>
            ) : (
              <section className="belvedere-offer__block">
                <p className="belvedere-offer__hotel-only">
                  חבילת מלון בלבד (ללא טיסות)
                </p>
              </section>
            )}

            <section
              className="belvedere-offer__block belvedere-offer__stay"
              aria-label="פרטי מלון וחדר"
            >
              <div className="belvedere-offer__section-head">
                <BedDouble size={18} aria-hidden />
                <h2>מלון וחדר</h2>
              </div>
              <OfferStayRooms offer={offer} nightsLabel={nightsLabel} />
            </section>

            {galleryNode}

            <OfferAgentCard agentBranding={offer.agentBranding} />
          </div>

          <aside className="belvedere-offer__pricing" aria-label="סיכום מחיר">
            <h2>סיכום לתשלום</h2>

            <div className="belvedere-offer__total belvedere-offer__total--hero">
              <div className="belvedere-offer__total-copy">
                <span>סה״כ לתשלום</span>
                <p className="belvedere-offer__total-hint">{resolvedTotalHint}</p>
              </div>
              <strong>{formatIls(offer.finalSalePriceIls)}</strong>
            </div>

            {(offer.occupancyLabel || offer.nights > 0) && (
              <p className="belvedere-offer__pricing-meta">
                {[
                  offer.occupancyLabel,
                  offer.nights > 0
                    ? `${offer.nights} ${offer.nights === 1 ? "לילה" : "לילות"}`
                    : null,
                  offer.roomName,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            )}

            {Array.isArray(offer.roomDetails) &&
            offer.roomDetails.length > 1 &&
            offer.roomDetails.some(
              (room) =>
                Number.isFinite(Number(room.packageWeightedIls)) &&
                Number(room.packageWeightedIls) > 0,
            ) ? (
              <div
                className="belvedere-offer__room-shares"
                aria-label="פירוט לפי חדר"
              >
                <h3 className="belvedere-offer__room-shares-title">
                  פירוט לפי חדר
                </h3>
                {offer.roomDetails.map((room) => {
                  const shareIls = Number(room.packageWeightedIls);
                  if (!(Number.isFinite(shareIls) && shareIls > 0)) return null;
                  return (
                    <div
                      className="belvedere-offer__price-line"
                      key={`room-share-${room.roomIndex}`}
                    >
                      <span>{formatOfferRoomBreakdownLabel(room)}</span>
                      <strong>{formatIls(shareIls)}</strong>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {offer.baggageLines?.length > 0 ? (
              <div className="belvedere-offer__baggage" aria-label="כבודה נוספת">
                <h3 className="belvedere-offer__baggage-title">כולל כבודה נוספת</h3>
                {offer.baggageLines.map((line, idx) => (
                  <div
                    className="belvedere-offer__price-line"
                    key={`${line.name}-${idx}`}
                  >
                    <span>{line.name}</span>
                    <strong>{formatIls(line.priceIls)}</strong>
                  </div>
                ))}
              </div>
            ) : null}

            {(offer.customerPassengerPricing ?? offer.passengerPricing)?.lines
              ?.length ? (
              <div className="belvedere-offer__pricing-breakdown">
                <CampaignPassengerPricingSummary
                  passengerPricing={
                    offer.customerPassengerPricing ?? offer.passengerPricing
                  }
                  formatPrice={formatIls}
                  title="פירוט לפי נוסע"
                  compact
                  showPackageTotal={false}
                  variant="receipt"
                />
              </div>
            ) : null}

            {primaryCta?.type === "link" && primaryCta.to ? (
              <Link
                to={primaryCta.to}
                className="belvedere-btn belvedere-btn--primary belvedere-offer__cta"
              >
                {primaryCta.label}
              </Link>
            ) : null}
            {primaryCta?.type === "leadForm" ? (
              <OfferLeadInterestForm primaryCta={primaryCta} />
            ) : null}
            {primaryCta?.type === "button" ? (
              <button
                type="button"
                className="belvedere-btn belvedere-btn--primary belvedere-offer__cta"
                onClick={primaryCta.onClick}
                disabled={Boolean(primaryCta.disabled || primaryCta.loading)}
              >
                {primaryCta.done
                  ? primaryCta.doneLabel || "תודה, פנייתך נרשמה"
                  : primaryCta.loading
                    ? primaryCta.loadingLabel || "שולחים…"
                    : primaryCta.label}
              </button>
            ) : null}

            {secondaryLink?.to ? (
              <Link to={secondaryLink.to} className="belvedere-offer__back">
                {secondaryLink.label}
              </Link>
            ) : null}

            {legalNote ? (
              <p className="belvedere-offer__legal-note">{legalNote}</p>
            ) : null}
          </aside>
        </main>
      </div>
    </div>
  );
}
